import React, { useEffect } from 'react';
import type { GetServerSideProps, NextPage } from 'next';
import NextImage from 'next/image';
import {
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  chakra,
  Flex,
  Text,
  Link,
} from '@chakra-ui/react';
import { ChevronRightIcon } from '@chakra-ui/icons';
import withAuth from '../../server/libs/withAuth';
import prisma from '../../server/libs/prisma';
import { sendAnalyticsEvent } from '../../common/libs/googleAnalytics';
import { getGaCid } from '../../server/libs/googleAnalytics';
import PageHead from '../../components/PageHead';
import { AntaaAccountType } from '../../common/libs/antaaAccountType';
import {
  findSpecialArticles,
  SpecialArticle,
} from '../../server/libs/specialArticles';
import { hasDemoAuthority } from '../../common/libs/userRoleType';
import PageUnavailableMsg from '../../components/PageUnavailableMsg';
import {
  isDoctorRecruiterAccount as isDoctorRecruiterAccountFunc,
  isPublisherAccount as isPublisherAccountFunc,
  isPharmaceuticalAccount as isPharmaceuticalAccountFunc,
} from '../../common/libs/user';
import { shouldShowSpecialPage } from '../../common/libs/specials';
import { generateS3DownloadUrl } from '../../server/libs/siteSettings';

// import {
//   SUUMO_SPECIAL_PAGE_NAME,
//   SUUMO_SPECIAL_PAGE_PATH,
// } from './residence-new-apartment';
// import { DYNAMICS_2024_PAGE_SLUG } from './dynamics2024';
// import { JIHIKEN_PAGE_SLUG } from './private-healthcare';
// import { setting as homeMedSetting } from './home-medical-care';
// import { setting as ibdUcCdSetting } from './ibd-uc-cd';
// import { setting as residentInternalMedicine } from './resident-internal-medicine';
// import { setting as designatedIntractableAndRareDiseasesSetting } from './designated-intractable-diseases-and-rare-diseases';
// import { setting as clinicalReasoningSetting } from './clinical-reasoning';

type PageContext = {
  isDemoAccount: boolean;
  isCorporationAccount: boolean;
  isPharmaceuticalAccount: boolean;
  specialArticles: SpecialArticle[];
};

const Breadcrumbs = () => {
  return (
    <Breadcrumb
      spacing="8px"
      separator={<ChevronRightIcon color="gray.500" />}
      fontSize="xs"
    >
      <BreadcrumbItem>
        <BreadcrumbLink
          href="/"
          onClick={() => sendAnalyticsEvent('breadcrumb', 'click', 'home')}
        >
          ホーム
        </BreadcrumbLink>
      </BreadcrumbItem>
    </Breadcrumb>
  );
};

async function createUserAccessHistory(
  userId?: number,
  userAgent?: string,
  gaCid?: string,
) {
  return await prisma.user_access_histories.create({
    data: {
      user_id: userId,
      ga_cid: gaCid,
      user_agent: userAgent,
      operation: 'special_index',
    },
  });
}

/**
 * バッジ表示
 */
export type BadgeType = 'preview';
const SpecialBadge = chakra(
  ({ badgeType, className }: { badgeType: BadgeType; className?: string }) => {
    let text;
    switch (badgeType) {
      case 'preview':
        text = 'プレビュー';
        break;
    }

    return (
      <Box
        className={className}
        display="inline-block"
        color="#2F855A"
        fontSize="x-small"
        fontWeight="bold"
        bg="#C6F6D5"
        borderRadius="full"
        px={3}
        py={1}
      >
        {text}
      </Box>
    );
  },
);

type SpecialCardProps = {
  articleId: string;
  externalUrl: string | null; // 外部リンクの時
  name: string | null;
  description: string | null;
  topImage: string | null; // /images/special/ 以下のファイル名
  isPrivate: boolean;
};

// 高カリウム血症特集のみクリックイベントに不規則な名前が使われていたため個別に指定
const extendedGaEventLabel: {
  [key: string]: string;
} = {
  fec15a463248d6a9: 'az_top',
};

const SpecialCard = ({
  articleId,
  name,
  externalUrl,
  description,
  topImage,
  isPrivate,
}: SpecialCardProps) => {
  const href = externalUrl ?? `/special/${articleId}`;
  const image = topImage?.includes('/')
    ? generateS3DownloadUrl(topImage)
    : `/images/special/${topImage}`;
  const isExternal = !!externalUrl;
  const gaLabel = extendedGaEventLabel[articleId] ?? articleId;

  return (
    <Box
      w={{ base: '100%', md: '31%' }}
      mb={4}
      borderRadius={3}
      borderColor="gray"
      borderWidth={1}
      _hover={{ opacity: '0.8' }}
      mr={{ base: 0, md: '2%' }}
      pos="relative"
    >
      <Link
        isExternal={isExternal}
        href={href}
        onClick={() => sendAnalyticsEvent('special', 'click', gaLabel)}
        _hover={{ textDecoration: 'none' }}
      >
        <NextImage
          src={image}
          alt={articleId}
          width={1300}
          height={720}
          priority
        />
        <Box pb={1} pl={1} pr={1}>
          <Text pb={1} fontWeight="bold" fontSize="xs" color="black">
            {name}
          </Text>
          <Text
            fontSize="xs"
            noOfLines={2}
            textOverflow="ellipsis"
            color="black"
          >
            {description}
          </Text>
        </Box>
      </Link>
      {isPrivate && (
        <SpecialBadge
          badgeType="preview"
          className="blinking"
          pos="absolute"
          top={2}
          right={2}
        />
      )}
    </Box>
  );
};

const SpecialPage: NextPage<PageContext> = ({
  specialArticles,
  isDemoAccount,
  isCorporationAccount,
  isPharmaceuticalAccount,
}: {
  specialArticles: SpecialArticle[];
  isDemoAccount: boolean;
  isCorporationAccount: boolean;
  isPharmaceuticalAccount: boolean;
}) => {
  // マウント時にイベント送信
  useEffect(() => {
    sendAnalyticsEvent('special', 'show', null);
  }, []);

  if ((isCorporationAccount && isPharmaceuticalAccount) || isDemoAccount) {
    return <PageUnavailableMsg isDemoAccount={isDemoAccount} />;
  }
  return (
    <>
      <PageHead
        description={'Antaa 特集'}
        imgUrlSuffix={'/images/special/special_top.png'}
        pageUrlSuffix={'/special'}
      />
      <Box id="top" pt={[14, 14, 14, 16]} bg="white">
        <Box
          py={4}
          pl={[4, 8, 0]}
          bg="white"
          w={{ base: '1024px', sm: '1024px' }}
        >
          <Breadcrumbs />
        </Box>
        <Box>
          <Box
            id="event"
            mt={[4, 2]}
            pr={[4, 6, 0]}
            pl={[4, 6, 0]}
            style={{ scrollMarginTop: '140px' }}
          >
            <Text mb={2} fontSize="ml" fontWeight="bold">
              特集一覧
            </Text>
            <Flex wrap="wrap">
              {specialArticles.map((article: SpecialArticle) => (
                <SpecialCard key={article.articleId} {...article} />
              ))}
            </Flex>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default SpecialPage;

export const getServerSideProps: GetServerSideProps<PageContext> = withAuth(
  async (context) => {
    const userId = context.req.user?.id;
    const userAgent = context.req?.headers['user-agent'] ?? '';
    await createUserAccessHistory(userId, userAgent, getGaCid(context.req));

    const specialArticlesFromDb = await findSpecialArticles();
    let specialArticles: SpecialArticle[] = specialArticlesFromDb
      .filter((article) => {
        if (
          article.discloseScope === 'PRIVATE' &&
          userId &&
          article.privateUsers?.includes(userId)
        ) {
          return true;
        }
        return article.discloseScope === 'PUBLIC';
      })
      .map((article) => ({
        articleId: article.articleId,
        displayOrder: article.displayOrder,
        name: article.name,
        description: article.description,
        topImage: article.topImage,
        externalUrl: article.externalUrl,
        isPrivate: article.discloseScope === 'PRIVATE',
      })) as SpecialArticle[];

    const isCorporationAccount =
      context.req.user?.antaaAccountType === AntaaAccountType.CORPORATION;
    const isPharmaceuticalAccount = isPharmaceuticalAccountFunc(
      context.req.user,
    );
    const isPublisherAccount = isPublisherAccountFunc(context.req.user);
    const isDoctorRecruiterAccount = isDoctorRecruiterAccountFunc(
      context.req.user,
    );
    const isDemoAccount = hasDemoAuthority(context.req.user?.userRoles);

    // 法人（医療機関）アカウントでは「在宅医療特集」のみ表示
    specialArticles = specialArticles.filter((article) =>
      shouldShowSpecialPage(
        article.articleId,
        isCorporationAccount,
        isPublisherAccount,
        isDoctorRecruiterAccount,
        isDemoAccount,
      ),
    );

    return {
      props: {
        specialArticles,
        isDemoAccount,
        isCorporationAccount,
        isPharmaceuticalAccount,
      },
    };
  },
);
